/**
 * Hook para busca de slots disponíveis e criação de booking (público)
 * 
 * Utilizado na página pública de agendamento para:
 * - Buscar event type pelo slug
 * - Buscar slots disponíveis via RPC get_available_scheduling_slots
 * - Criar booking via RPC create_scheduling_booking
 * 
 * RELEASE: 2026-02-14
 * VERSION: 1.0.0
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { getEdgeFunctionUrl } from '@/lib/supabaseHelpers';

const logger = createLogger('PublicScheduling');

// ============================================================================
// TIPOS
// ============================================================================

/** Slot disponível retornado pela RPC */
export interface AvailableSlot {
  slot_start: string;
  slot_end: string;
}

/** Dados do formulário de booking */
export interface BookingFormData {
  guest_name: string;
  guest_email: string;
  guest_phone?: string;
  guest_notes?: string;
}

/** Event type público (dados visíveis ao visitante) */
export interface PublicEventType {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  duration_minutes: number;
  color: string | null;
  timezone: string;
  min_notice_hours: number;
  max_advance_days: number;
  conferencing_provider: string;
  requires_approval: boolean;
  confirmation_message: string | null;
  logo_url: string | null;
  user_id: string;
  tenant_id: string;
}

// ============================================================================
// HOOK: Busca event type pelo slug
// ============================================================================

export function usePublicEventType(slug: string | undefined, tenantSlug: string | undefined) {
  return useQuery({
    queryKey: ['public-event-type', tenantSlug, slug],
    queryFn: async () => {
      if (!slug || !tenantSlug) return null;

      logger.info('Buscando event type público', { tenantSlug, slug });

      // Passo 1: Buscar tenant_id via RPC pública (bypassa RLS da tabela tenants)
      const { data: tenantId, error: tenantError } = await supabase
        .rpc('get_tenant_id_by_slug', { p_slug: tenantSlug });

      if (tenantError) {
        logger.error('Erro ao buscar tenant pelo slug via RPC', tenantError);
        throw tenantError;
      }

      if (!tenantId) {
        logger.warn('Tenant não encontrado pelo slug', { tenantSlug });
        return null;
      }

      // Passo 2: Buscar event type filtrando por slug + tenant_id (unicidade garantida)
      const { data, error } = await supabase
        .from('scheduling_event_types')
        .select('id, title, slug, description, duration_minutes, color, timezone, min_notice_hours, max_advance_days, conferencing_provider, requires_approval, confirmation_message, logo_url, user_id, tenant_id')
        .eq('slug', slug)
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        logger.error('Erro ao buscar event type', error);
        throw error;
      }

      return data as PublicEventType | null;
    },
    enabled: !!slug && !!tenantSlug,
  });
}

// ============================================================================
// HOOK: Busca slots disponíveis para uma data
// ============================================================================

export function useAvailableSlots(
  eventTypeId: string | undefined,
  date: string | null,
  timezone: string = 'America/Sao_Paulo'
) {
  return useQuery({
    queryKey: ['available-slots', eventTypeId, date, timezone],
    queryFn: async () => {
      if (!eventTypeId || !date) return [];

      logger.info('Buscando slots disponíveis', { eventTypeId, date });

      const { data, error } = await supabase.rpc('get_available_scheduling_slots', {
        p_event_type_id: eventTypeId,
        p_date: date,
        p_timezone: timezone,
      });

      if (error) {
        logger.error('Erro ao buscar slots', error);
        throw error;
      }

      return (data || []) as AvailableSlot[];
    },
    enabled: !!eventTypeId && !!date,
  });
}

// ============================================================================
// HOOK: Cria booking
// ============================================================================

export function useCreatePublicBooking() {
  return useMutation({
    mutationFn: async (params: {
      eventTypeId: string;
      startAt: string;
      formData: BookingFormData;
      timezone: string;
      dealId?: string; // NOVO: Deal vinculado ao agendamento
    }) => {
      logger.info('Criando booking público', {
        eventTypeId: params.eventTypeId,
        startAt: params.startAt,
        dealId: params.dealId,
      });

      const { data, error } = await supabase.rpc('create_scheduling_booking', {
        p_event_type_id: params.eventTypeId,
        p_start_at: params.startAt,
        p_guest_name: params.formData.guest_name,
        p_guest_email: params.formData.guest_email,
        p_guest_phone: params.formData.guest_phone || null,
        p_guest_notes: params.formData.guest_notes || null,
        p_timezone: params.timezone,
        p_deal_id: params.dealId || null, // NOVO: Passa deal_id quando disponível
      });

      if (error) {
        logger.error('Erro ao criar booking', error);
        throw error;
      }

      const bookingId = data as string;

      // Fire-and-forget: chamar scheduling-webhook para criar evento + enviar email
      try {
        const webhookUrl = getEdgeFunctionUrl('scheduling-webhook');
        fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ booking_id: bookingId }),
        }).catch((err) => {
          logger.warn('Erro ao chamar scheduling-webhook (fire-and-forget)', err);
        });
      } catch (webhookErr) {
        logger.warn('Erro ao disparar scheduling-webhook', webhookErr);
      }

      return bookingId;
    },
  });
}

// ============================================================================
// HOOK: Busca contexto do deal para pré-preencher formulário público
// ============================================================================

/** Dados do contato vinculado ao deal */
export interface DealSchedulingContext {
  guest_name: string;
  guest_email: string;
  guest_phone: string;
}

// ============================================================================
// HOOK: Cancela booking via cancel_token (público)
// ============================================================================

/** Resultado do cancelamento retornado pela RPC */
export interface CancelBookingResult {
  booking_id: string;
  event_title: string;
  start_at: string;
  guest_name: string;
  google_event_id: string | null;
  user_id: string;
}

export function useCancelBooking() {
  return useMutation({
    mutationFn: async (params: {
      cancelToken: string;
      reason?: string;
    }) => {
      logger.info('Cancelando booking via cancel_token');

      const { data, error } = await supabase.rpc('cancel_scheduling_booking', {
        p_cancel_token: params.cancelToken,
        p_reason: params.reason || null,
      });

      if (error) {
        logger.error('Erro ao cancelar booking', error);
        throw error;
      }

      const result = data as unknown as CancelBookingResult;

      // Fire-and-forget: deletar evento do Google Calendar se existir
      if (result.google_event_id && result.user_id) {
        try {
          const calendarUrl = getEdgeFunctionUrl('calendar-provider');
          fetch(calendarUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'delete-event',
              user_id: result.user_id,
              google_event_id: result.google_event_id,
            }),
          }).catch((err) => {
            logger.warn('Erro ao deletar evento do Calendar (fire-and-forget)', err);
          });
        } catch (calErr) {
          logger.warn('Erro ao disparar delete-event no Calendar', calErr);
        }
      }

      return result;
    },
  });
}

// ============================================================================
// HOOK: Verifica booking existente para deal + event_type (evita duplicação)
// ============================================================================

/** Booking ativo existente para um deal específico */
export interface ExistingBooking {
  id: string;
  start_at: string;
  end_at: string;
  guest_name: string;
  guest_email: string;
  status: string;
  cancel_token: string;
  created_at: string;
}

export function useExistingDealBooking(dealId: string | null, eventTypeId: string | undefined) {
  return useQuery({
    queryKey: ['existing-deal-booking', dealId, eventTypeId],
    queryFn: async () => {
      if (!dealId || !eventTypeId) return null;

      logger.info('Verificando booking existente para deal', { dealId, eventTypeId });

      const { data, error } = await supabase.rpc('get_existing_booking_by_deal', {
        p_deal_id: dealId,
        p_event_type_id: eventTypeId,
      });

      if (error) {
        logger.error('Erro ao buscar booking existente por deal', error);
        throw error;
      }

      return data as unknown as ExistingBooking | null;
    },
    enabled: !!dealId && !!eventTypeId,
  });
}

// ============================================================================
// HOOK: Busca contexto do deal para pré-preencher formulário público
// ============================================================================

// ============================================================================
// HOOK: Busca datas bloqueadas para desabilitar no calendário público
// ============================================================================

// ============================================================================
// HOOK: Busca dias da semana indisponíveis para desabilitar no calendário
// ============================================================================

export function useUnavailableWeekdays(eventTypeId: string | undefined) {
  return useQuery({
    queryKey: ['scheduling-unavailable-weekdays', eventTypeId],
    queryFn: async () => {
      if (!eventTypeId) return [];

      logger.info('Buscando dias da semana indisponíveis', { eventTypeId });

      const { data, error } = await supabase.rpc(
        'get_scheduling_unavailable_weekdays',
        { p_event_type_id: eventTypeId }
      );

      if (error) {
        logger.error('Erro ao buscar dias indisponíveis', error);
        throw error;
      }

      return (data || []) as number[];
    },
    enabled: !!eventTypeId,
  });
}

export function useBlockedDates(eventTypeId: string | undefined) {
  return useQuery({
    queryKey: ['scheduling-blocked-dates', eventTypeId],
    queryFn: async () => {
      if (!eventTypeId) return [];

      logger.info('Buscando datas bloqueadas', { eventTypeId });

      const { data, error } = await supabase.rpc('get_scheduling_blocked_dates', {
        p_event_type_id: eventTypeId,
      });

      if (error) {
        logger.error('Erro ao buscar datas bloqueadas', error);
        throw error;
      }

      // Retorna array de strings 'YYYY-MM-DD'
      return (data || []).map((row: { blocked_date: string }) => row.blocked_date);
    },
    enabled: !!eventTypeId,
  });
}

export function usePublicDealContext(dealId: string | null) {
  return useQuery({
    queryKey: ['deal-scheduling-context', dealId],
    queryFn: async () => {
      if (!dealId) return null;

      logger.info('Buscando contexto do deal para agendamento', { dealId });

      const { data, error } = await supabase.rpc('get_deal_scheduling_context', {
        p_deal_id: dealId,
      });

      if (error) {
        logger.error('Erro ao buscar contexto do deal', error);
        throw error;
      }

      return data as unknown as DealSchedulingContext;
    },
    enabled: !!dealId,
  });
}
